/**
 * UPS-specific claim info collected from the merchant at claim creation
 * time. Includes dropdown enums + zod schemas for everything the UPS batch
 * claim xlsx needs in its damage section (columns Q–AJ).
 *
 * Single source of truth for:
 *   - UI dropdowns (render from the enum values)
 *   - Zod schemas stored on ShippingClaim.carrierSpecificInfo.ups
 *   - xlsx file writer (enum values are emitted verbatim into the sheet)
 *
 * Yes/No toggles are `z.boolean()` at the model layer; the xlsx writer
 * translates to "Yes"/"No" at serialization. Template column R ("Will
 * photos be provided?") is intentionally omitted — it's computed from
 * whether the claim has photos attached.
 */
import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

// ---------------------------------------------------------------------------
// Dropdown enums (labels must match the UPS template Hidden Sheet exactly)
// ---------------------------------------------------------------------------

/** Column X — Current Location of the Damaged Package */
export enum UpsDamageLocation {
  AT_SHIPPER = "At Shipper's Location",
  AT_CONSIGNEE = "At Consignee's Location",
  AT_OTHER = "At Other Location",
}
export const UpsDamageLocationSchema = createEnum(UpsDamageLocation, {
  enumName: "UpsDamageLocation",
}).meta({ privacy: privacy.nonPii });

/** Column AC — Condition of Damaged Item(s) */
export enum UpsItemCondition {
  BENT = "Bent",
  CRACKED = "Cracked",
  CRUSHED = "Crushed",
  DENTED = "Dented",
  PUNCTURE = "Puncture",
  SCRATCHED = "Scratched",
  SHATTERED = "Shattered",
  SOILED = "Soiled",
  SPOILED = "Spoiled",
  TORN = "Torn",
  WET = "Wet",
  OTHER = "Other",
}
export const UpsItemConditionSchema = createEnum(UpsItemCondition, {
  enumName: "UpsItemCondition",
}).meta({ privacy: privacy.nonPii });

/** Column AE — Package Type */
export enum UpsPackageType {
  BOX = "Box",
  BAG = "Bag",
  CASE = "Case",
  ENVELOPE = "Envelope",
  PAIL = "Pail/Bucket/Can",
  WRAP = "Plastic or Paper-Wrap",
  TUBE = "Tube",
  OTHER = "Other",
}
export const UpsPackageTypeSchema = createEnum(UpsPackageType, {
  enumName: "UpsPackageType",
}).meta({ privacy: privacy.nonPii });

/** Column AH — How was Package Sealed? */
export enum UpsSealMethod {
  CABLE = "Cable",
  CAP = "Cap",
  CLASP = "Clasp",
  GLUE = "Glue",
  HINGE = "Hinge",
  LATCH = "Latch",
  NAILS = "Nails",
  NONE = "None",
  STAPLES = "Staples",
  STITCH = "Stitch",
  STRAPS = "Straps",
  STRING = "String",
  TAPE = "Tape",
  TIES = "Ties",
  TWIST = "Twist",
  ZIPPER = "Zipper",
}
export const UpsSealMethodSchema = createEnum(UpsSealMethod, {
  enumName: "UpsSealMethod",
}).meta({ privacy: privacy.nonPii });

/**
 * Column AI — Packaging Materials.
 * UPS spells these as camel-ish tokens ("AirBags", "DoubleBoxed",
 * "LooseFill") verbatim from the Hidden Sheet — do not prettify or UPS
 * rejects the row.
 */
export enum UpsPackagingMaterial {
  AIR_BAGS = "AirBags",
  WRAP = "Wrap",
  CORRUGATED = "Corrugated",
  DOUBLE_BOXED = "DoubleBoxed",
  FOAM = "Foam",
  LOOSE_FILL = "LooseFill",
  PAPER = "Paper",
  PEANUTS = "Peanuts",
  NONE = "None",
  OTHER = "Other",
}
export const UpsPackagingMaterialSchema = createEnum(UpsPackagingMaterial, {
  enumName: "UpsPackagingMaterial",
}).meta({ privacy: privacy.nonPii });

// ---------------------------------------------------------------------------
// Damage section inputs (columns Q–AJ)
// ---------------------------------------------------------------------------

export const UpsDamageInfoSchema = z.object({
  /** Q — Both contents and packaging available for inspection? */
  contentsAndPackagingAvailable: z.boolean().meta({ privacy: privacy.nonPii }),
  /** S — Date damage was discovered (not in the future) */
  dateDamageDiscovered: z.date().meta({ privacy: privacy.nonPii }),
  /** T — Total items in the package (≤9999) */
  totalItemsInPackage: z
    .number()
    .int()
    .min(1)
    .max(9999)
    .meta({ privacy: privacy.nonPii }),
  /** U — Are items missing from the package? */
  itemsMissing: z.boolean().meta({ privacy: privacy.nonPii }),
  /** V — Contains dangerous goods? */
  containsDangerousGoods: z.boolean().meta({ privacy: privacy.nonPii }),
  /** W — Can package be sealed and transported safely? */
  canBeSealedSafely: z.boolean().meta({ privacy: privacy.nonPii }),
  /** X — Current location of the damaged package */
  currentLocation: UpsDamageLocationSchema.meta({ privacy: privacy.nonPii }),
  /** Y — Contact name at "Other Location" (conditional on X, ≤35) */
  otherLocationContactName: z
    .string()
    .nullish()
    .meta({ privacy: privacy.person_name.thirdParty }),
  /** Z — City at "Other Location" (conditional, ≤50) */
  otherLocationCity: z.string().nullish().meta({ privacy: privacy.nonPii }),
  /** AA — Postal code at "Other Location" (conditional, ≤8) */
  otherLocationPostal: z.string().nullish().meta({ privacy: privacy.nonPii }),
  /** AB — Country code at "Other Location" (conditional) */
  otherLocationCountry: z.string().nullish().meta({ privacy: privacy.nonPii }),
  /** AC — Condition of damaged item(s) */
  itemCondition: UpsItemConditionSchema.meta({ privacy: privacy.nonPii }),
  /** AD — "Other" condition description (conditional on itemCondition=OTHER, ≤50) */
  itemConditionOther: z
    .string()
    .nullish()
    .meta({ privacy: privacy.text.merchant }),
  /** AE — Package type */
  packageType: UpsPackageTypeSchema.meta({ privacy: privacy.nonPii }),
  /** AF — "Other" package type (conditional on packageType=OTHER, ≤50) */
  packageTypeOther: z
    .string()
    .nullish()
    .meta({ privacy: privacy.text.merchant }),
  /** AG — Damage to outside of package? */
  damageToOutside: z.boolean().meta({ privacy: privacy.nonPii }),
  /** AH — How was package sealed? */
  howPackageSealed: UpsSealMethodSchema.meta({ privacy: privacy.nonPii }),
  /** AI — Packaging materials */
  packagingMaterials: UpsPackagingMaterialSchema.meta({
    privacy: privacy.nonPii,
  }),
  /** AJ — "Other" packaging materials (conditional, ≤50) */
  packagingMaterialsOther: z
    .string()
    .nullish()
    .meta({ privacy: privacy.text.merchant }),
});
export type UpsDamageInfo = z.infer<typeof UpsDamageInfoSchema>;

export function missingUpsDamageFields(
  d: Partial<UpsDamageInfo>,
): (keyof UpsDamageInfo)[] {
  const required: (keyof UpsDamageInfo)[] = [
    "contentsAndPackagingAvailable",
    "dateDamageDiscovered",
    "totalItemsInPackage",
    "itemsMissing",
    "containsDangerousGoods",
    "canBeSealedSafely",
    "currentLocation",
    "itemCondition",
    "packageType",
    "damageToOutside",
    "howPackageSealed",
    "packagingMaterials",
  ];
  if (d.currentLocation === UpsDamageLocation.AT_OTHER) {
    required.push(
      "otherLocationContactName",
      "otherLocationCity",
      "otherLocationPostal",
      "otherLocationCountry",
    );
  }
  if (d.itemCondition === UpsItemCondition.OTHER) {
    required.push("itemConditionOther");
  }
  if (d.packageType === UpsPackageType.OTHER) {
    required.push("packageTypeOther");
  }
  if (d.packagingMaterials === UpsPackagingMaterial.OTHER) {
    required.push("packagingMaterialsOther");
  }
  return required.filter((key) => d[key] !== false && !d[key]);
}

export function isUpsDamageInfoComplete(
  d: Partial<UpsDamageInfo>,
): d is UpsDamageInfo {
  return missingUpsDamageFields(d).length === 0;
}

/** Column T accepts 1–9999, so a missing or absurd count still files. */
function clampItemCount(itemCount: number): number {
  return Math.min(Math.max(Math.trunc(itemCount) || 1, 1), 9999);
}

/**
 * Neutral Q–AJ answers for a damaged claim the merchant never filled in. UPS
 * requires the section, so "Other" plus free text beats holding the claim.
 */
export function defaultUpsDamageInfo(
  discoveredAt: Date,
  itemCount: number,
): UpsDamageInfo {
  return {
    contentsAndPackagingAvailable: true,
    dateDamageDiscovered: discoveredAt,
    totalItemsInPackage: clampItemCount(itemCount),
    itemsMissing: false,
    containsDangerousGoods: false,
    canBeSealedSafely: true,
    currentLocation: UpsDamageLocation.AT_CONSIGNEE,
    itemCondition: UpsItemCondition.OTHER,
    itemConditionOther: "Damaged in transit",
    packageType: UpsPackageType.BOX,
    damageToOutside: true,
    howPackageSealed: UpsSealMethod.TAPE,
    packagingMaterials: UpsPackagingMaterial.OTHER,
    packagingMaterialsOther: "Unknown",
  };
}

/**
 * UPS-specific claim info. Currently only carries the damage section; more
 * UPS fields may be added (e.g. whether the merchant has already filed
 * directly with UPS, InsureShield policy number if covered, etc.).
 */
export const UpsClaimInfoSchema = z.object({
  damage: UpsDamageInfoSchema.nullish().meta({ privacy: privacy.structured }),
});
export type UpsClaimInfo = z.infer<typeof UpsClaimInfoSchema>;
